import { c as _c } from "react/compiler-runtime";
import { Stack, Typography } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { compact } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { Button } from '@/components/form/baseInputs/Button';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { Alert } from '@/components/notifications/Alert/Alert';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { CellContainer } from '@/components/tables/DataTable/components/cells';
import { DataTableCard } from '@/components/tables/DataTable/components/DataTableCard';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { IconButtonRenderer } from '@/components/tables/DataTable/renderers/cell/IconButtonRenderer';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { TableEmptyState } from '@/components/tables/DataTable/TableEmptyState';
import { Column } from '@/components/tables/DataTable/types';
import { useReportError } from '@/hooks/useReportError';
import { useSalesforceBrand } from '@/modules/salesforce/brand';
import { ROUTE_KEYS } from '@/navigation/constants';
import { formatDateToMMDDYY } from '@/utils/formatting/dates';
import { getNodes } from '@/utils/graphqlUtils';
import { useSalesforceConnectionQuery } from '../AdminIntegrationsPage/graphql/SalesforceIntegration.generated';
import { AdminAiPromptsNavigation, AI_PROMPTS_DESCRIPTION } from './AdminAiPromptsNavigation';
import { AdminFormsDocument, CreateFormMutationVariables, useAdminFormsQuery, useCreateFormMutation, useNormalizeFormDraftConfigMutation } from './AdminFormsPage.generated';
import { createFormDraftInput, FormDraftInput, FormStarter, getFormFields, hydrateFormDraft } from './AdminFormsPage.model';
import { FormJsonImportModal } from './FormJsonImportModal';
import { FormVersionMenu } from './FormVersionMenu';
import { SalesforceFormImportModal } from './salesforce/SalesforceFormImportModal';
interface PublishedFormVersion {
  id: string;
  version: number;
}
interface FormRow {
  id: string;
  name: string;
  state: string;
  fieldCount: string;
  formVersion: string;
  currentVersion: number | null;
  versions: PublishedFormVersion[];
  updatedAt: string;
  icon?: never;
}
interface FormNameRecord {
  name: string;
}
const slots = {
  noRowsOverlay: () => <TableEmptyState text="No reviews" />
};
export function getAvailableFormName(baseName: string, existingForms: readonly FormNameRecord[]): string {
  const existingNames = new Set(existingForms.map(form => form.name));
  if (!existingNames.has(baseName)) return baseName;
  let suffix = 1;
  while (existingNames.has(`${baseName} (${suffix})`)) {
    suffix += 1;
  }
  return `${baseName} (${suffix})`;
}
export function getCreateFormVariables(starter: FormStarter | FormDraftInput, existingForms: readonly FormNameRecord[] = []): CreateFormMutationVariables {
  const draft = typeof starter === 'string' ? createFormDraftInput(starter) : starter;
  return {
    input: {
      create: {
        ...draft,
        name: getAvailableFormName(draft.name, existingForms)
      }
    }
  };
}
export function AdminFormsPage() {
  const $ = _c(48);
  const [isStarterModalOpen, setIsStarterModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isSalesforceModalOpen, setIsSalesforceModalOpen] = useState(false);
  const [normalize] = useNormalizeFormDraftConfigMutation();
  const {
    navigate
  } = useNavigateToRoute();
  const {
    showFeedback
  } = useFeedback();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      fetchPolicy: "cache-and-network"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data,
    error,
    loading
  } = useAdminFormsQuery(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      fetchPolicy: "cache-and-network"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: salesforceConnectionData
  } = useSalesforceConnectionQuery(t1);
  const isSalesforceConnected = salesforceConnectionData?.salesforceConnection?.connected === true;
  useReportError("Failed to load forms", error);
  const {
    reportError
  } = useReportError();
  let t2;
  if ($[2] !== reportError || $[3] !== showFeedback) {
    t2 = {
      onError: mutationError => {
        showFeedback("Failed to create form", {
          variant: "error"
        });
        reportError("Failed to create form", mutationError);
      }
    };
    $[2] = reportError;
    $[3] = showFeedback;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [createForm, t3] = useCreateFormMutation(t2);
  const {
    loading: isCreating
  } = t3;
  let t4;
  if ($[5] !== navigate) {
    t4 = formId => navigate(ROUTE_KEYS.ADMIN_FORM_EDITOR, {
      formId
    });
    $[5] = navigate;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const openForm = t4;
  const columns = useFormTableColumns(openForm);
  data?.forms;
  const t5 = data?.forms;
  let t6;
  if ($[7] !== t5) {
    t6 = getNodes(t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const formRecords = t6;
  let t7;
  if ($[9] !== formRecords) {
    t7 = compact(formRecords.map(_temp));
    $[9] = formRecords;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const rows = t7;
  let t8;
  if ($[11] !== createForm || $[12] !== formRecords || $[13] !== navigate) {
    t8 = async starter => {
      const result = await createForm({
        variables: getCreateFormVariables(starter, formRecords),
        refetchQueries: [AdminFormsDocument],
        awaitRefetchQueries: true
      });
      const formId_0 = result.data?.createForm.id;
      if (!formId_0) {
        return;
      }
      setIsStarterModalOpen(false);
      setIsSalesforceModalOpen(false);
      navigate(ROUTE_KEYS.ADMIN_FORM_EDITOR, {
        formId: formId_0
      });
    };
    $[11] = createForm;
    $[12] = formRecords;
    $[13] = navigate;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const startForm = t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Button size="sm" variant="secondary" startIcon={PlusIcon} onClick={() => setIsStarterModalOpen(true)}>Add review</Button>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <AdminAiPromptsNavigation />;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== error) {
    t11 = error && <Alert severity="error">Reviews could not be loaded. Refresh the page to try again.</Alert>;
    $[17] = error;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== columns || $[20] !== error || $[21] !== loading || $[22] !== openForm || $[23] !== rows) {
    t12 = !error && <DataTableCard><DataTable columns={columns} rows={rows} loading={loading} pagination={false} alwaysShowPaginationFooter={true} slots={slots} onRowClick={row => openForm(row.row.id)} /></DataTableCard>;
    $[19] = columns;
    $[20] = error;
    $[21] = loading;
    $[22] = openForm;
    $[23] = rows;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t11 || $[26] !== t12) {
    t13 = <Stack spacing={2}>{t10}{t11}{t12}</Stack>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== isCreating || $[29] !== isSalesforceConnected || $[30] !== isStarterModalOpen || $[31] !== startForm) {
    t14 = isStarterModalOpen && <FormStarterModal isCreating={isCreating} isSalesforceConnected={isSalesforceConnected} onClose={() => setIsStarterModalOpen(false)} onSelect={startForm} onImport={() => {
      setIsStarterModalOpen(false);
      setIsImportModalOpen(true);
    }} onSalesforce={() => {
      setIsStarterModalOpen(false);
      setIsSalesforceModalOpen(true);
    }} />;
    $[28] = isCreating;
    $[29] = isSalesforceConnected;
    $[30] = isStarterModalOpen;
    $[31] = startForm;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== isCreating || $[34] !== isSalesforceModalOpen || $[35] !== startForm) {
    t15 = isSalesforceModalOpen && <SalesforceFormImportModal isCreating={isCreating} onClose={() => setIsSalesforceModalOpen(false)} onCreate={startForm} />;
    $[33] = isCreating;
    $[34] = isSalesforceModalOpen;
    $[35] = startForm;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== createForm || $[38] !== formRecords || $[39] !== isImportModalOpen || $[40] !== normalize || $[41] !== openForm) {
    t16 = isImportModalOpen && <FormJsonImportModal mode="create" onClose={() => setIsImportModalOpen(false)} onApply={async json => {
      const normalized = await normalize({
        variables: {
          json
        }
      });
      if (!normalized.data) {
        throw new Error("Normalization returned no data");
      }
      const variables = getCreateFormVariables("scratch", formRecords);
      variables.input.create.draftConfig = normalized.data.normalizeFormDraftConfig;
      const result_0 = await createForm({
        variables,
        refetchQueries: [AdminFormsDocument],
        awaitRefetchQueries: true
      });
      if (!result_0.data?.createForm.id) {
        throw new Error("Creation returned no form");
      }
      openForm(result_0.data.createForm.id);
    }} />;
    $[37] = createForm;
    $[38] = formRecords;
    $[39] = isImportModalOpen;
    $[40] = normalize;
    $[41] = openForm;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t13 || $[44] !== t14 || $[45] !== t15 || $[46] !== t16) {
    t17 = <SubpageLayout heading="AI prompts" description={AI_PROMPTS_DESCRIPTION} headingSpacing={3} actions={t9}>{t13}{t14}{t15}{t16}</SubpageLayout>;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  return t17;
}
function _temp(form) {
  const draftConfig = form.draftConfig;
  const hydratedForm = hydrateFormDraft({
    ...form,
    draftConfig
  });
  if (!hydratedForm) {
    return;
  }
  const currentVersion = hydratedForm.currentPublishedVersion?.version ?? null;
  return {
    id: hydratedForm.id,
    name: hydratedForm.name,
    state: currentVersion ? "Published" : "Draft",
    fieldCount: String(getFormFields(hydratedForm.draftConfig.form).length),
    formVersion: currentVersion ? String(currentVersion) : "Not published",
    currentVersion,
    versions: getNodes(form.versions),
    updatedAt: formatDateToMMDDYY(new Date(hydratedForm.updatedAt))
  };
}
function useFormTableColumns(openForm) {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      field: "name",
      headerName: "Review",
      flex: 2,
      renderCell: TextRenderer()
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      field: "state",
      headerName: "State",
      flex: 1,
      renderCell: TextRenderer()
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      field: "fieldCount",
      headerName: "Fields",
      flex: 0.6,
      renderCell: TextRenderer()
    };
    t3 = {
      field: "formVersion",
      headerName: "Version",
      flex: 1,
      renderCell: _temp2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      field: "updatedAt",
      headerName: "Updated",
      flex: 0.8,
      renderCell: TextRenderer()
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== openForm) {
    t5 = IconButtonRenderer({
      iconButtonProps: t6 => {
        const {
          row
        } = t6;
        return {
          icon: Edit02Icon,
          size: "xs",
          variant: "transparent",
          ariaLabel: `Edit ${row.name}`,
          handleClick: () => openForm(row.id)
        };
      }
    });
    $[5] = openForm;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5) {
    t6 = [t0, t1, t2, t3, t4, {
      field: "icon",
      headerName: "",
      align: "center",
      sortable: false,
      width: 64,
      renderCell: t5
    }];
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
function _temp2(params) {
  if (!params.row.currentVersion) {
    return TextRenderer()(params);
  }
  return <CellContainer align={params.colDef.align}><FormVersionMenu formName={params.row.name} currentVersion={params.row.currentVersion} versions={params.row.versions} /></CellContainer>;
}
interface FormStarterModalProps {
  isCreating: boolean;
  isSalesforceConnected: boolean;
  onClose: () => void;
  onSelect: (starter: FormStarter) => Promise<void>;
  onImport: () => void;
  onSalesforce: () => void;
}
function FormStarterModal(t0) {
  const $ = _c(27);
  const {
    isCreating,
    isSalesforceConnected,
    onClose,
    onSelect,
    onImport,
    onSalesforce
  } = t0;
  const brand = useSalesforceBrand();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Typography variant="body-s">Choose how to start the review.</Typography>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onSelect) {
    t2 = async () => onSelect("scratch");
    $[1] = onSelect;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isCreating || $[4] !== t2) {
    t3 = <StarterButton title="Start from scratch" description="Create an empty draft config and add fields yourself." disabled={isCreating} onClick={t2} />;
    $[3] = isCreating;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== onSelect) {
    t4 = async () => onSelect("trustReview");
    $[6] = onSelect;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isCreating || $[9] !== t4) {
    t5 = <StarterButton title="Trust review" description="Start with representative trust-review fields." disabled={isCreating} onClick={t4} />;
    $[8] = isCreating;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== isCreating || $[12] !== onImport) {
    t6 = <StarterButton title="Create from JSON" description="Import a draft config from JSON." disabled={isCreating} onClick={onImport} />;
    $[11] = isCreating;
    $[12] = onImport;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== brand || $[15] !== isCreating || $[16] !== isSalesforceConnected || $[17] !== onSalesforce) {
    t7 = isSalesforceConnected && <StarterButton title={`Create from ${brand.name}`} description={`Start with sections and questions from a ${brand.name} page layout.`} disabled={isCreating} onClick={onSalesforce} />;
    $[14] = brand;
    $[15] = isCreating;
    $[16] = isSalesforceConnected;
    $[17] = onSalesforce;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t3 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
    t8 = <Stack spacing={1.5}>{t1}{t3}{t5}{t6}{t7}</Stack>;
    $[19] = t3;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== onClose || $[25] !== t8) {
    t9 = <DialogModal isOpen={true} heading="Add review" onClose={onClose}>{t8}</DialogModal>;
    $[24] = onClose;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}
interface StarterButtonProps {
  title: string;
  description: string;
  disabled: boolean;
  onClick: () => void;
}
function StarterButton(t0) {
  const $ = _c(12);
  const {
    title,
    description,
    disabled,
    onClick
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: "100%"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== title) {
    t2 = <Typography variant="title-xs">{title}</Typography>;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== description) {
    t3 = <Typography variant="body-xs">{description}</Typography>;
    $[3] = description;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <Stack alignItems="flex-start" textAlign="left" spacing={0.25}>{t2}{t3}</Stack>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== disabled || $[9] !== onClick || $[10] !== t4) {
    t5 = <Button size="md" variant="secondary" fullWidth={true} whiteSpace="normal" contentBoxSx={t1} disabled={disabled} onClick={onClick}>{t4}</Button>;
    $[8] = disabled;
    $[9] = onClick;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}